<template>
  <span class="text-left subtitle-2 font-weight-bold d-inline-block">
    <transition
      name="price-change"
      mode="out-in"
    >
      <span
        :key="formattedPrice"
        class="gradient-text"
      >{{ displayValue }}</span>
    </transition>
  </span>
</template>

<script>
import currencyMixins from '@/mixins/currencyMixins';
import TaxRates from '@/graphql/TaxRates';
import { supportedCountries, DEFAULT_TAX_REGION } from '@/utils/Const';

export default {
	name: "PriceComponent",
  mixins: [currencyMixins],
  props: {
    bookPrice: {
      type: Number,
      required: true,
    },
    baseCurrency: {
      type: String,
      required: true,
    },
    addTax: {
      type: Boolean,
      default: false,
    },
    showFreeText: {
      type: Boolean,
      default: true,
    },
    shouldRound: {
      type: Boolean,
      default: false,
    },
    isOwned: {
      type: Boolean,
      default: false,
    },
    quantity: {
      type: Number,
      default: 1,
    },
    showInBaseCurrency: {
      type: Boolean,
      default: false,
    },
    skipBaseRounding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formattedPrice: '',
      rate: 0,
    };
  },
  computed: {
    selectedCurrencyCode() {
      return this.showInBaseCurrency ? this.baseCurrency : this.getCurrencyCode();
    },
    displayValue() {
      if (this.isOwned) return this.$t('bookshelf.owned');
      if (this.bookPrice == 0 && this.showFreeText) return this.$t('shop.free');
      return this.formattedPrice || this.$t('common.loading');
    },
    taxRegion() {
      const storedRegion = this.$store.state.locale.countryCode;

      if (supportedCountries.find((item) => item.alpha2 === storedRegion || item.alpha3 === storedRegion)) {
        return storedRegion;
      }
      else {
        return DEFAULT_TAX_REGION;
      }
    },
  },
  watch: {
    selectedCurrencyCode: 'updateFormattedPrice',
    baseCurrency: 'updateFormattedPrice',
    addTax: 'updateFormattedPrice',
    rate: 'updateFormattedPrice',
    bookPrice: 'updateFormattedPrice',
    quantity: 'updateFormattedPrice',
    skipBaseRounding: 'updateFormattedPrice',
  },
  created() {
    this._priceUpdateSeq = 0;
    const seq = this._priceUpdateSeq;
    this.priceString().then((result) => {
      if (seq !== this._priceUpdateSeq) return;
      if (result !== this.formattedPrice) {
        this.formattedPrice = result;
      }
    });
  },
  apollo: {
    taxRate: {
      query: TaxRates.getTaxRate,
      variables() {
        return {
          id: "/tax_rates/"+this.taxRegion,
        };
      },
      update(data) {
        const newRate = data.taxRate?.rate ?? 0;
        if (this.rate !== newRate) {
          this.rate = newRate;
        }
        return data;
      },
      skip() {
        return !this.addTax;
      },
    },
  },
  methods: {
    async updateFormattedPrice() {
      const seq = ++this._priceUpdateSeq;
      const result = await this.priceString();
      if (seq !== this._priceUpdateSeq) return;
      if (result !== this.formattedPrice) {
        this.formattedPrice = result;
      }
    },
    async priceString() {
      if (this.bookPrice == null) {
        return this.$t("common.loading");
      } else {
        if (this.showInBaseCurrency) {
          const price = Number(this.bookPrice);
          const formattedPrice = await this.getPriceString(this.baseCurrency, price, this.shouldRound);
          return formattedPrice;
        } else {
          const effectivePrice = Number(this.bookPrice);
          const effectiveTaxRate = this.addTax ? this.rate : 0;

          const price = await this.getPrice(this.baseCurrency, effectivePrice, effectiveTaxRate, this.shouldRound, this.quantity, this.skipBaseRounding);
          const formattedPrice = await this.getPriceString(this.getCurrencyCode(), price, this.shouldRound);
          return formattedPrice;
        }
      }
    },
  },
};
</script>

